// pages/ViewTask.js
import React, { useEffect, useState } from "react";
import draftToHtml from 'draftjs-to-html';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { deleteTaskById, getTaskByID } from "../../services/api/TaskApi";
import {
  FiTag, FiCalendar, FiFileText, FiCheckCircle, FiXCircle,
  FiClock, FiUser, FiUserPlus, FiLink, FiActivity
} from "react-icons/fi";
import { BiEdit, BiTrash } from "react-icons/bi";
import "./ViewTask.scss";
import { getUserData } from "../../services/api/AuthApi";
import { useLoading } from "../../context/LoadingContext";
import { formatPartyNames, formatText } from "../../components/TruncateParties";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";

const ViewTask = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate()
  const { parties } = location.state || {};
  const [assignedByName, setAssignedByName] = useState("");
  const [assignedToNames, setAssignedToNames] = useState([]);

  const [task, setTask] = useState(null);
  const { showLoading, hideLoading } = useLoading();
    const [showDeleteModal, setShowDeleteModal] = useState(false);

  

  useEffect(() => {
    if (id) {
      showLoading('Loading...');
      //setLoading(true);
      getTaskByID(id)
        .then(async (res) => {
          const taskData = res.data;
          setTask(taskData);

          try {
            if (taskData?.assignedBy) {
              const resBy = await getUserData(taskData?.assignedBy);
              setAssignedByName(resBy.data?.name || taskData?.assignedBy);
            }

            if (Array.isArray(taskData?.assignedTo)) {
              const assignedToPromises = taskData?.assignedTo.map((uid) => getUserData(uid));
              const assignedToResponses = await Promise.all(assignedToPromises);
              const names = assignedToResponses.map((res) => res.data?.name || "");
              setAssignedToNames(names);
            }
          } catch (err) {
            console.error("Failed to fetch user data", err);
          }

          hideLoading();
        })

    }
  }, [id]);

  const handleEdit = () => {
    navigate(`/home/tasks/edit/${id}`);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    showLoading("Deleting task...");
    try {
      await deleteTaskById(id);
      navigate('/home/tasks');
    } catch (error) {
      console.error("Failed to delete task:", error);
    } finally {
      hideLoading();
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };


  const renderStatusLogs = () => {
    if (!task?.taskStatusLogs || task.taskStatusLogs.length === 0) {
      return <p>No status changes recorded</p>;
    }

    // Sort logs by creation date (newest first)
    const sortedLogs = [...task.taskStatusLogs].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    return (
      <div className="status-logs">
        {sortedLogs.map((log, index) => (
          <div key={log._id || index} className="status-log-item">
            <div className="status-log-icon">
              {log.status ? (
                <FiCheckCircle className="icon success" />
              ) : (
                <FiXCircle className="icon danger" />
              )}
            </div>
            <div className="status-log-content">
              <div className="status-log-header">
                <span className={`status-badge ${log.status ? 'completed' : 'incomplete'}`}>
                  {log.status ? 'Marked as Completed' : 'Marked as Incomplete'}
                </span>
                <span className="status-log-date">
                  {new Date(log.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="status-log-user">
                By: {formatText(log.userName) || 'Unknown User'}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // if (loading) {
  //   return (
  //     <div className="task-detail-container">
  //       <div className="loading-spinner"></div>
  //     </div>
  //   );
  // }

  if (!task) {
    return (
      <div className="task-detail-container">
        <div className="error-message">Task not found</div>
      </div>
    );
  }



  return (
    <div className="task-detail-container">
      <div className="task-card">
        <div className="task-header">
          <div className="task-header-data">
            <div className="avatar">
              {task.taskName?.charAt(0).toUpperCase()}
            </div>
            <div className="task-title">
              <h2>{formatText(task.taskName)}</h2>
              <span className={`task-status ${task.isCompleted ? 'active' : 'inactive'}`}>
                {task.isCompleted ? 'Completed' : 'Incomplete'}
              </span>
            </div>
          </div>
          <div className="client-actions">
            <button className="btn-update" onClick={handleEdit}>
              <BiEdit /> Update
            </button>
            <button className="btn-delete" onClick={handleDelete}>
              <BiTrash /> Delete
            </button>
          </div>
        </div>

        <div className="task-details-grid">
          <div className="view-task-detail-item">
            <FiTag className="icon" />
            <div style={{ cursor: task.courtRef ? 'pointer' : 'default' }} onClick={task.courtRef ? () => navigate(`/home/cases/casedetails/${task.courtRef}`) : undefined}>
              <label>Court Ref</label>
              <p>{task.courtRef ? `${task.courtRef}-(${formatPartyNames(parties)})` : "Court Ref is Not Provided"}</p>
            </div>
          </div>

          <div className="view-task-detail-item">
            <FiCalendar className="icon" />
            <div>
              <label>Due Date</label>
              <p>{new Date(task.dueDate).toLocaleString()}</p>
            </div>
          </div>

          <div className="view-task-detail-item">
            <FiClock className="icon" />
            <div>
              <label>Created At</label>
              <p>{new Date(task.createdAt).toLocaleString()}</p>
            </div>
          </div>

          <div className="view-task-detail-item">
            <FiUser className="icon" />
            <div>
              <label>Assigned By</label>
              <p>{formatText(assignedByName) || 'Unknown'}</p>
            </div>
          </div>

          <div className="view-task-detail-item">
            <FiUserPlus className="icon" />
            <div>
              <label>Assigned To</label>
              <p>{assignedToNames.length ? assignedToNames.join(', ') : 'Unassigned'}</p>
            </div>
          </div>

          {task.document && (
            <div className="view-task-detail-item">
              <FiLink className="icon" />
              <div>
                <label>Document</label>
                <a href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${task.document}`} target="_blank" rel="noopener noreferrer">
                  View Document
                </a>
              </div>
            </div>
          )}
          <div className="view-task-detail-item status-logs-section task-description">
            <FiFileText className="icon" />
            <div>
              <label>Description</label>
              {task.description ? (
                (() => {
                  let html = '';
                  try {
                    const raw = typeof task.description === 'string' ? JSON.parse(task.description) : task.description;
                    if (raw && raw.blocks) {
                      html = draftToHtml(raw);
                    } else {
                      html = task.description;
                    }
                  } catch (e) {
                    html = task.description;
                  }
                  // Fix anchor tags to always use absolute URLs
                  let fixedHtml = html.replace(/<a ([^>]*href=["'])(?!https?:\/\/)([^"'>]+)(["'][^>]*)>/gi, '<a $1https://$2$3>');
                  
                  fixedHtml = fixedHtml.replace(/<a /gi, '<a target="_blank" rel="noopener noreferrer" ');
                  return <div dangerouslySetInnerHTML={{ __html: fixedHtml }} />;
                })()
              ) : (
                <p>No description provided</p>
              )}
            </div>
          </div>

          {/* Status Logs Section */}
          <div className="view-task-detail-item status-logs-section task-status-history">
            <FiActivity className="icon" />
            <div>
              <label>Status History</label>
              {renderStatusLogs()}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        itemType={`task "${task?.taskName}"`}
      />
    </div>
  );
};

export default ViewTask;